import React, { useEffect } from 'react'
import { Box, Container, Paper, Button, Typography, Link, Alert } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ArrowBack } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { usePageContentWidth } from '../../../contexts/PageContentWidthContext'
import { useRegisterForm } from './hooks/useRegisterForm'
import { RegisterFormFields } from './components/RegisterFormFields'
import { LanguageSelectors } from './components/LanguageSelectors'
import type { SelectChangeEvent } from '@mui/material'

const RegisterPage: React.FC = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { setPageContentWidth } = usePageContentWidth()
    // Invite mode is enforced server-side: registration rejects a missing
    // invite key with INVITE_KEY_REQUIRED, which is surfaced inline on the
    // field. We intentionally avoid a mount-time public-config request so
    // unauthenticated visitors make no backend call on /register load (TRA-116).
    const inviteKeyRequired = false
    const {
        formData,
        error,
        inviteKeyError,
        loading,
        showPassword,
        showConfirmPassword,
        touchedFields,
        handleChange,
        handleSelectChange,
        handleTogglePasswordVisibility,
        handleToggleConfirmPasswordVisibility,
        handleSubmit,
    } = useRegisterForm({ navigate, inviteKeyRequired })

    // Set page content width to 'sm' on mount
    useEffect(() => {
        setPageContentWidth('sm')
        return () => {
            setPageContentWidth(null)
        }
    }, [setPageContentWidth])

    const handleSelectChangeWithType = (e: SelectChangeEvent) => {
        handleSelectChange(e)
    }

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
                    <Box sx={{ mb: 2 }}>
                        <Link
                            component={RouterLink}
                            to="/"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: 'primary.main',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            <ArrowBack sx={{ mr: 1, fontSize: '1.2rem' }} />
                            {t('common:backToHome')}
                        </Link>
                    </Box>
                    <Typography component="h1" variant="h4" align="center" gutterBottom>
                        {t('common:register')}
                    </Typography>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            role="alert"
                            aria-live="assertive"
                            id="register-error"
                        >
                            {error}
                        </Alert>
                    )}

                    <Box
                        component="form"
                        noValidate
                        onSubmit={(e) => {
                            void handleSubmit(e)
                        }}
                        aria-label={t('auth:signUpForm')}
                        sx={{ mt: 2 }}
                    >
                        <RegisterFormFields
                            email={formData.email}
                            username={formData.username}
                            password={formData.password}
                            confirmPassword={formData.confirmPassword}
                            inviteKey={formData.inviteKey}
                            inviteKeyRequired={inviteKeyRequired}
                            inviteKeyError={inviteKeyError}
                            showPassword={showPassword}
                            showConfirmPassword={showConfirmPassword}
                            touchedFields={touchedFields}
                            onEmailChange={handleChange}
                            onUsernameChange={handleChange}
                            onPasswordChange={handleChange}
                            onConfirmPasswordChange={handleChange}
                            onInviteKeyChange={handleChange}
                            onTogglePasswordVisibility={handleTogglePasswordVisibility}
                            onToggleConfirmPasswordVisibility={
                                handleToggleConfirmPasswordVisibility
                            }
                        />

                        <LanguageSelectors
                            userLang={formData.userLang}
                            targetLang={formData.targetLang}
                            onUserLangChange={handleSelectChangeWithType}
                            onTargetLangChange={handleSelectChangeWithType}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={loading}
                        >
                            {loading ? t('common:loading') : t('auth:signUp')}
                        </Button>
                        <Box
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="body2">
                                {t('auth:hasAccount')}{' '}
                                <Link component={RouterLink} to="/login">
                                    {t('auth:signIn')}
                                </Link>
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                textAlign: 'center',
                                mt: 2,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                {t('privacy:legal.termsAndPrivacy')}
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    )
}

export default RegisterPage
